import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ProjectDetailCard, 
  EpicDetailCard, 
  FeatureDetailCard,
  ProjectList,
  ImportDrawer
} from "./components";
import {
  Project,
  Epic,
  Feature,
  getCompletionPercentage,
  convertStatusFormat
} from "./components/SourceUtils";
import {
  getStatusIcon,
  getStatusColor,
  getStatusBorderColor,
  getProgressColor,
  formatStatusForDisplay
} from "./components/StatusUtils";
import { 
  HiOutlineChevronDown, 
  HiOutlineChevronRight, 
  HiOutlineCalendar, 
  HiOutlineClock, 
  HiOutlineExclamationCircle, 
  HiOutlineXCircle, 
  HiOutlineCheckCircle, 
  HiOutlineCollection, 
  HiOutlineDotsHorizontal, 
  HiOutlinePlus, 
  HiOutlineUsers,
  HiViewList,
  HiChartBar,
  HiDownload,
  HiRefresh
} from "react-icons/hi";
import { SiMicrosoftazure, SiJira, SiAsana } from "react-icons/si";
import { Button, Card, Dropdown, Progress, Badge, Avatar } from "flowbite-react";
import ImportProjectModal from "../../components/Modals/ImportProjectModal";
import CreateRoadMapsModal from "../RoadMapNew/CreateRoadMapsModal";
import SearchInput from "../../components/common/Search/SearchInput";
import SingleSelect from "../../components/common/Search/SingleSelect";
import CustomButton from "../../components/common/CustomButton";
import SourceDropdown from "../../components/common/SourceDropdown";
import { useDashboardService } from "../../services/dashboard";

// Interface for roadmap group from API
interface RoadmapGroup {
  id: string;
  name: string;
  description: string;
  source: string;
  start_date: string;
  end_date: string;
  progress: number;
  people_allocated: number;
  status: string;
  stats: {
    total_roadmaps: number;
    on_track_roadmaps: number;
    completed_roadmaps: number;
    delayed_roadmaps: number;
    blocked_roadmaps: number;
  };
  roadmaps: Roadmap[];
  entity_type?: string;
  child_entity_type?: string;
  last_updated?: string;
  updated_by?: string;
}

// Interface for roadmap from API
interface Roadmap {
  id: string;
  name: string;
  status: string;
  progress: number;
  start_date: string;
  end_date: string;
}

const ProjectView = () => {
  const navigate = useNavigate();
  const dashboardService = useDashboardService();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [selectedEpic, setSelectedEpic] = useState<Epic | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [expandedEpicId, setExpandedEpicId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("All Statuses");
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Add source filter state with predefined known sources
  // const knownSources = ["azure", "jira", "asana", "monday", "cadence"];
  const knownSources = ["azure"];
  const [availableSources, setAvailableSources] = useState<string[]>(knownSources);
  const [selectedSources, setSelectedSources] = useState<string[]>(knownSources);
  
  // State for API data
  const [apiRoadmapGroups, setApiRoadmapGroups] = useState<RoadmapGroup[]>([]);
  const [currentRoadmapGroup, setCurrentRoadmapGroup] = useState<RoadmapGroup | null>(null);
  const [currentRoadmap, setCurrentRoadmap] = useState<any>(null);
  const [currentRoadmapProject, setCurrentRoadmapProject] = useState<any>(null);
  
  // Loading states
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [isProjectLoading, setIsProjectLoading] = useState<boolean>(false);
  const [isEpicLoading, setIsEpicLoading] = useState<boolean>(false);
  const [isFeatureLoading, setIsFeatureLoading] = useState<boolean>(false);
  
  // Status options for the dropdown
  const statusOptions = [
    { value: "All Statuses", label: "All Statuses" },
    { value: "on_track", label: "On Track" },
    { value: "delayed", label: "Delayed" },
    { value: "completed", label: "Completed" },
    { value: "blocked", label: "Blocked" },
    { value: "not_started", label: "Not Started" }
  ];
  
  const [popoverEpic, setPopoverEpic] = useState<{ epic: Epic; position: { top: number; left: number } } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<'breakdown'>('breakdown');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRoadmap, setSelectedRoadmap] = useState<{} | null>(null);
  
  // Show all sources under one heading
  const [showAllSources, setShowAllSources] = useState<boolean>(true);

  const [showImportDrawer, setShowImportDrawer] = useState(false);
  const [selectedImportSource, setSelectedImportSource] = useState<string>("");

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsDataLoading(true);
      try {
        const data = await dashboardService.fetchDashboard();
        if (data && data.roadmap_groups) {
          setApiRoadmapGroups(data.roadmap_groups);
          
          // Add any new sources found in the API data to our list
          const apiSources = data.roadmap_groups.map((group: RoadmapGroup) => group.source.toLowerCase());
          const uniqueSources = Array.from(new Set([...knownSources, ...apiSources])) as string[];
          setAvailableSources(uniqueSources);
          setSelectedSources(uniqueSources); // Select all sources by default
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsDataLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  useEffect(() => {
    // Add click outside listener for popover
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setPopoverEpic(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if(!showCreateModal)
      setSelectedRoadmap({});
  }, [showCreateModal]);

  // Convert RoadmapGroup to Project format for API sources
  const convertRoadmapGroupsToProjects = (roadmapGroups: RoadmapGroup[]): Project[] => {
    return roadmapGroups.map(group => ({
      id: group.id,
      name: group.name,
      description: group.description || "",
      source: group.source,
      startDate: new Date(group.start_date).toLocaleDateString(),
      endDate: new Date(group.end_date).toLocaleDateString(),
      status: convertStatusFormat(group.status),
      peopleAllocated: group.people_allocated,
      epics: [],
      blockers: 0,
      completionPercentage: group.progress * 100,
      tasks: group.stats.total_roadmaps || 0,
      tasksCompleted: group.stats.completed_roadmaps || 0
    }));
  };

  // Add a function to filter projects by status properly
  const filterProjectsByStatus = (projects: Project[]) => {
    if (!statusFilter || statusFilter === "All Statuses") {
      return projects;
    }
    
    // Match the API status format with our filter value
    return projects.filter(project => {
      const projectStatus = project.status.toLowerCase().replace(' ', '_');
      return projectStatus === statusFilter.toLowerCase();
    });
  };

  // Add a function to filter projects by source
  const filterProjectsBySource = (projects: Project[]) => {
    if (!selectedSources || selectedSources.length === 0) {
      return projects;
    }
    
    return projects.filter(project => {
      if (!project.source) return false;
      return selectedSources.includes(project.source.toLowerCase());
    });
  };

  // Update the getAllProjects function to use both filters
  const getAllProjects = (): Project[] => {
    // Use only API data from all sources
    const allProjects = convertRoadmapGroupsToProjects(apiRoadmapGroups);
    const filteredBySource = filterProjectsBySource(allProjects);
    return filterProjectsByStatus(filteredBySource);
  };

  const toggleProject = async (projectId: string) => {
    // Check if this project exists in API data
    const isApiProject = apiRoadmapGroups.some(group => group.id === projectId);
    
    if (isApiProject) {
      if (expandedProjectId === projectId) {
        // Closing project
        setExpandedProjectId(null);
        setSelectedProject(null);
        setCurrentRoadmapGroup(null);
      } else {
        // Find the project in API data first
        const apiProject = convertRoadmapGroupsToProjects(apiRoadmapGroups)
          .find(p => p.id === projectId);
        
        // Immediately set the project and expandedProjectId to show UI without delay
        setExpandedProjectId(projectId);
        setSelectedProject(apiProject || null);
        
        // Then fetch data in the background
        setIsProjectLoading(true);
        
        // Fetch roadmap group details in the background
        dashboardService.fetchRoadmapGroupDashboard(projectId)
          .then(groupData => {
            setCurrentRoadmapGroup(groupData);
          })
          .catch(error => {
            console.error("Error fetching roadmap group data:", error);
          })
          .finally(() => {
            setIsProjectLoading(false);
          });
      }
    } else {
      // Handle non-API projects (should not happen anymore)
      console.warn("Attempted to toggle a non-API project");
    }
    
    setSelectedEpic(null);
    setSelectedFeature(null);
    setPopoverEpic(null);
    setExpandedEpicId(null);
  };

  // Convert roadmap to Epic format for display
  const convertRoadmapToEpic = (roadmap: Roadmap): Epic => {
    return {
      id: roadmap.id,
      name: roadmap.name,
      description: "",
      status: convertStatusFormat(roadmap.status),
      progress: roadmap.progress * 100,
      startDate: new Date(roadmap.start_date).toLocaleDateString(),
      endDate: new Date(roadmap.end_date).toLocaleDateString(),
      features: [],
      blockers: 0
    };
  };

  const selectEpic = async (epic: Epic) => {
    setSelectedEpic(epic);
    setSelectedFeature(null);
    setCurrentRoadmapProject(null);
    setExpandedEpicId(null);
    
    // Fetch roadmap details when selecting epic
    setIsEpicLoading(true);
    try {
      const roadmapData = await dashboardService.fetchRoadmapDashboard(epic.id);
      setCurrentRoadmap(roadmapData);
    } catch (error) {
      console.error("Error fetching roadmap data:", error);
    } finally {
      setIsEpicLoading(false);
    }
  };

  const toggleEpic = async (epic: Epic) => {
    // Check if this is an API roadmap epic
    const isApiEpic = currentRoadmapGroup !== null;
    
    if (isApiEpic) {
      if (expandedEpicId === epic.id) {
        // Closing epic
        setExpandedEpicId(null);
      } else {
        // Expand the epic list and select the epic to update the epic card
        setExpandedEpicId(epic.id);
        setSelectedEpic(epic); // Select the epic to show epic card
        
        // Fetch roadmap details when expanding
        setIsEpicLoading(true);
        try {
          const roadmapData = await dashboardService.fetchRoadmapDashboard(epic.id);
          setCurrentRoadmap(roadmapData);
        } catch (error) {
          console.error("Error fetching roadmap data:", error);
        } finally {
          setIsEpicLoading(false);
        }
      }
    } else {
      // Handle non-API epics (should not happen anymore)
      console.warn("Attempted to toggle a non-API epic");
    }
  };

  const selectFeature = async (feature: Feature) => {
    // If empty feature id, we're deselecting
    if (!feature.id) {
      setSelectedFeature(null);
      setCurrentRoadmapProject(null);
      return;
    }
    
    // Immediately set the feature to show UI without delay
    setSelectedFeature(feature);
    
    // Check if this is an API project feature
    const isApiFeature = currentRoadmap !== null;
    
    if (isApiFeature && feature.id) {
      // Then fetch project details in the background
      setIsFeatureLoading(true);
      
      // Fetch project details in the background
      dashboardService.fetchProjectDashboard(feature.id)
        .then(projectData => {
          setCurrentRoadmapProject(projectData);
        })
        .catch(error => {
          console.error("Error fetching project data:", error);
        })
        .finally(() => {
          setIsFeatureLoading(false);
        });
    }
    
    setPopoverEpic(null);
  };

  const openDashboard = (projectId: string) => {
    console.log("Opening dashboard for project", projectId);
  };
  
  // Add these empty functions back to satisfy the ProjectList component props
  const openRoadmap = () => {};
  const openTasks = () => {};
  
  const handleImportSourceClick = (source: string) => {
    setSelectedImportSource(source);
  };
  
  const handleCloseImportDrawer = () => {
    setShowImportDrawer(false);
    setSelectedImportSource("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusChange = (option: any) => {
    if (option) {
      setStatusFilter(option.value);
    }
  };
  
  // Handle toggling sources in the source dropdown
  const handleToggleSource = (source: string) => {
    if (selectedSources.includes(source)) {
      // Don't allow deselecting all sources
      if (selectedSources.length <= 1) return;
      setSelectedSources(selectedSources.filter(s => s !== source));
    } else {
      setSelectedSources([...selectedSources, source]);
    }
  };

  return (
    <div className="flex flex-col h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#111928] dark:to-[#0f172a]">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 w-full">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Project Dashboard & Analytics</h1>
              <span className="text-sm text-slate-500 dark:text-slate-400">Track your project progress and performance in one place</span>
            </div>
            <div className="flex items-center gap-2">
              <CustomButton 
                onClick={() => setShowImportDrawer(true)}
                leftIcon={<HiDownload className="text-xl" />}
                className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
              >
                Import Projects
              </CustomButton>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-600 shadow-md sticky top-[73px] z-10 w-full">
        <div className="w-full px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label htmlFor="search" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Search:
                </label>
                <SearchInput
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search projects..."
                  width="w-64"
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Status:
                </label>
                <SingleSelect
                  name="status"
                  options={statusOptions}
                  placeholder="Select status"
                  onChange={handleStatusChange}
                  defaultValue={statusOptions.find(option => option.value === statusFilter)}
                  width="w-48"
                />
              </div>
            </div>
            
            {/* Move Source Dropdown to the end */}
            <div className="flex items-center">
              <SourceDropdown
                sources={availableSources}
                selectedSources={selectedSources}
                onToggleSource={handleToggleSource}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto w-full">
        <div className="w-full px-6 py-6">
          <div className="space-y-8">
            {/* Display all projects under one "Workspace Sources" heading */}
            <div className="w-full bg-white dark:bg-[#1e293b] rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <ProjectList
                projects={getAllProjects()}
                selectedProject={selectedProject}
                expandedProjectId={expandedProjectId}
                selectedEpic={selectedEpic}
                selectedFeature={selectedFeature}
                expandedEpicId={expandedEpicId}
                statusFilter={statusFilter}
                searchQuery={searchQuery}
                setShowCreateModal={setShowCreateModal}
                toggleProject={toggleProject}
                toggleEpic={toggleEpic}
                selectEpic={selectEpic}
                selectFeature={selectFeature}
                openDashboard={openDashboard}
                openRoadmap={openRoadmap}
                openTasks={openTasks}
                heading="Azure Projects"
                currentRoadmapGroup={currentRoadmapGroup}
                currentRoadmap={currentRoadmap}
                currentRoadmapProject={currentRoadmapProject}
                isLoading={isDataLoading}
                isProjectLoading={isProjectLoading}
                isEpicLoading={isEpicLoading}
                isFeatureLoading={isFeatureLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Import Drawer */}
      <ImportDrawer 
        isOpen={showImportDrawer}
        onClose={handleCloseImportDrawer}
      />

      {/* Create Strategic Map Modal/Drawer */}
      {showCreateModal && (
        <CreateRoadMapsModal 
          isVisible={showCreateModal}
          setShowModal={setShowCreateModal}
          roadmap={selectedRoadmap}
          setSelectedRoadmap={setSelectedRoadmap}
        />
      )}
    </div>
  );
};

export default ProjectView;